import NetworkManager from './NetworkManager';
import type {
  ApiResponse
} from '@byte-store-control/shared';

// Authentication API
export const redeemMappToken = async (mappToken: string): Promise<any> => {
  const response = await NetworkManager.post('permissions', { mappToken });
  return response;
};

// Alias for compatibility
export const redeemAuthToken = redeemMappToken;

// Channel APIs using REST endpoints
export const getStoreChannels = async (storeNumber: string): Promise<any> => {
  const response = await NetworkManager.get(`stores/${storeNumber}/channels`);
  return response;
};

export const getStoresChannelsBulk = async (storeNumbers: string[]): Promise<any> => {
  const response = await NetworkManager.post('stores/channels/bulk', { storeNumbers });
  return response;
};

export const updateStoreOnlineOrders = async (
  storeNumber: string,
  settings: {
    acceptingOnlineOrders?: boolean;
    appearInStoreSearch?: boolean;
  }
): Promise<any> => {
  const response = await NetworkManager.patch(
    `stores/${storeNumber}/online-orders`,
    settings
  );
  return response;
};

export const toggleChannelStatus = async (
  storeNumber: string,
  channel: string,
  activate: boolean,
  deactivationReason?: string,
  deactivationExpiry?: string
): Promise<any> => {
  const response = await NetworkManager.post(
    `stores/${storeNumber}/channels/toggle`,
    {
      channel,
      activate,
      deactivationReason,
      deactivationExpiry
    }
  );
  return response;
};

// Available channel configuration for the org
export interface AvailableChannel {
  channel: string;
  displayName: string;
  defaultMarkupPercent?: number;
  maxMarkupPercent?: number;
  minMarkupPercent?: number;
}

export const getAvailableChannels = async (): Promise<AvailableChannel[]> => {
  const response = await NetworkManager.get('channels/available');
  return response.data || [];
};

// Item Availability APIs
export interface MenuItemBlackout {
  __typename: string;
  startingAt: string;
  expiry: string;
  reason: 'OUT_OF_STOCK' | 'COMING_SOON';
  details?: string;
  productCode?: string;
  variantCode?: string;
  optionValueCode?: string;
  modifierCode?: string;
  bundleCode?: string;
}

export interface ItemAvailabilityResponse {
  blackouts: MenuItemBlackout[];
  pageInfo: {
    hasNextPage: boolean;
  };
}

export const getStoreItemAvailability = async (
  storeNumber: string,
  first?: number,
  after?: string
): Promise<ItemAvailabilityResponse> => {
  const params = new URLSearchParams();
  if (first) params.append('first', first.toString());
  if (after) params.append('after', after);

  const response = await NetworkManager.get(
    `stores/${storeNumber}/item-availability?${params.toString()}`
  );
  return response.data || { blackouts: [], pageInfo: { hasNextPage: false } };
};

export const createItemBlackout = async (
  storeNumbers: string[],
  productCodes: string[],
  bundleCodes: string[],
  reason: 'OUT_OF_STOCK' | 'COMING_SOON',
  expiry: string,
  startingAt?: string,
  variantCodes: string[] = [],
  modifierCodes: string[] = [],
  optionValueCodes: string[] = [],
  items?: Array<{ type: string; code: string; name: string }> // Items with names for audit
): Promise<MenuItemBlackout[]> => {
  const response = await NetworkManager.post('stores/item-availability', {
    storeNumbers,
    productCodes,
    bundleCodes,
    variantCodes,
    modifierCodes,
    optionValueCodes,
    reason,
    expiry,
    startingAt: startingAt || new Date().toISOString(),
    items, // Include items with names for audit logging
  });
  return response.data || [];
};

export const deleteItemBlackout = async (
  storeNumber: string,
  type: 'PRODUCT' | 'VARIANT' | 'OPTION_VALUE' | 'MODIFIER' | 'BUNDLE',
  itemCode: string
): Promise<MenuItemBlackout[]> => {
  // DELETE requests with body need to pass data in config
  const response = await NetworkManager.delete(`stores/${storeNumber}/item-availability`, {
    data: {
      type,
      itemCode
    }
  });
  return response.data || [];
};

/**
 * Item with code and name for display
 */
export interface ItemWithName {
  code: string;
  name: string;
}

/**
 * Bulk delete item blackouts (activate items) across multiple stores
 */
export interface BulkDeleteItemBlackoutRequest {
  storeNumbers: string[];
  productCodes?: string[];
  bundleCodes?: string[];
  variantCodes?: string[];
  modifierCodes?: string[];
  optionValueCodes?: string[];
  // Items with names for audit logging
  items?: Array<{ type: string; code: string; name: string }>;
}

export interface BulkDeleteItemBlackoutResponse {
  status: boolean;
  summary: {
    total: number;
    succeeded: number;
    failed: number;
  };
  results: Array<{
    storeNumber: string;
    success: boolean;
    error?: string;
  }>;
  message: string;
}

export const bulkDeleteItemBlackouts = async (
  request: BulkDeleteItemBlackoutRequest
): Promise<BulkDeleteItemBlackoutResponse> => {
  const response = await NetworkManager.post('stores/item-availability/bulk-delete', request);
  return response;
};

// Store Hours APIs
export type OccasionType = 'DELIVERY' | 'CARRYOUT' | 'DINE_IN' | 'DRIVE_THRU' | 'CATERING_CARRYOUT';

export interface TimeSlot {
  open: string; // HH:MM format
  close: string; // HH:MM format
}

export interface DayHours {
  enabled: boolean;
  timeSlots: TimeSlot[];
}

export interface StoreHoursInput {
  occasion: OccasionType;
  isEnabled?: boolean;
  days: {
    MONDAY?: DayHours;
    TUESDAY?: DayHours;
    WEDNESDAY?: DayHours;
    THURSDAY?: DayHours;
    FRIDAY?: DayHours;
    SATURDAY?: DayHours;
    SUNDAY?: DayHours;
  };
}

export const updateStoreOccasionHours = async (
  storeNumber: string,
  hoursData: StoreHoursInput
): Promise<any> => {
  const response = await NetworkManager.patch(
    `/stores/${storeNumber}/hours`,
    hoursData
  );
  return response;
};

// Special Hours APIs
export interface SpecialHoursInput {
  occasion: OccasionType;
  startDate: string; // ISO date string
  endDate: string; // ISO date string
  isClosed: boolean;
  openTime?: string; // HH:MM format
  closeTime?: string; // HH:MM format
}

export const addSpecialHours = async (
  storeNumber: string,
  specialHours: SpecialHoursInput
): Promise<any> => {
  const response = await NetworkManager.post(
    `/stores/${storeNumber}/special-hours`,
    specialHours
  );
  return response;
};

export interface DeleteSpecialHoursInput {
  occasion: OccasionType;
  startDate: string; // YYYY-MM-DD format
  endDate: string; // YYYY-MM-DD format
}

export const deleteSpecialHours = async (
  storeNumber: string,
  deleteInput: DeleteSpecialHoursInput
): Promise<any> => {
  const response = await NetworkManager.delete(
    `/stores/${storeNumber}/special-hours`,
    { data: deleteInput }
  );
  return response;
};

// Health check (REST endpoint)
export const checkHealth = async (): Promise<ApiResponse<any>> => {
  try {
    const response = await NetworkManager.get<ApiResponse<any>>('/health');
    return response;
  } catch (error) {
    return {
      success: false,
      error: 'Health check failed',
      timestamp: new Date().toISOString(),
    };
  }
};

// =============================================================================
// Bulk Operations Types and APIs
// =============================================================================

/**
 * Error category for bulk operation failures
 */
export type BulkOperationErrorCategory = 'TRANSIENT' | 'PERMANENT' | 'PARTIAL';

/**
 * Result of a single store operation within a bulk operation
 */
export interface StoreOperationResult<T = unknown> {
  storeNumber: string;
  success: boolean;
  data?: T;
  error?: {
    message: string;
    code?: string;
    category: BulkOperationErrorCategory;
    details?: unknown;
  };
  retryAttempts?: number;
}

/**
 * Summary of bulk operation results
 */
export interface BulkOperationSummary {
  total: number;
  succeeded: number;
  failed: number;
  retryable: number;
  skipped?: number;
}

/**
 * Overall status of a bulk operation
 */
export type BulkOperationStatus = 'success' | 'partial_success' | 'failure';

/**
 * Complete response from a bulk operation
 */
export interface BulkOperationResponse<T = unknown> {
  status: BulkOperationStatus;
  summary: BulkOperationSummary;
  results: StoreOperationResult<T>[];
  failedStoreNumbers: string[];
  retryableStoreNumbers: string[];
  skippedStoreNumbers?: string[];
  skipReason?: string;
  requestId: string;
  originalRequestId?: string; // Present in retry responses
}

/**
 * Input for bulk store hours update
 */
export interface BulkStoreHoursRequest {
  storeNumbers: string[];
  occasion: OccasionType;
  isEnabled?: boolean;
  days: {
    [day: string]: DayHours;
  };
}

/**
 * Input for bulk channel update
 */
export interface BulkChannelRequest {
  storeNumbers: string[];
  channel: string;
  activate: boolean;
  deactivationReason?: string;
  deactivationExpiry?: string;
}

/**
 * Bulk update store hours for multiple stores
 */
export const bulkUpdateStoreHours = async (
  request: BulkStoreHoursRequest
): Promise<BulkOperationResponse> => {
  const response = await NetworkManager.post('stores/hours/bulk-update', request);
  return response;
};

/**
 * Retry failed bulk store hours updates
 */
export const retryBulkStoreHours = async (
  originalRequestId: string,
  storeNumbers: string[],
  occasion: OccasionType,
  days: { [day: string]: DayHours }
): Promise<BulkOperationResponse> => {
  const response = await NetworkManager.post('stores/hours/bulk-retry', {
    originalRequestId,
    storeNumbers,
    occasion,
    days,
  });
  return response;
};

/**
 * Single special hours entry
 */
export interface SpecialHoursEntry {
  startDate: string; // ISO date string
  endDate: string; // ISO date string
  isClosed: boolean;
  openTime?: string; // HH:MM format
  closeTime?: string; // HH:MM format
}

/**
 * Bulk special hours request - supports multiple entries in one call
 */
export interface BulkSpecialHoursRequest {
  storeNumbers: string[];
  occasion: OccasionType;
  entries: SpecialHoursEntry[];
}

/**
 * Bulk add special hours for multiple stores (supports multiple entries)
 */
export const bulkAddSpecialHours = async (
  request: BulkSpecialHoursRequest
): Promise<BulkOperationResponse> => {
  const response = await NetworkManager.post('stores/hours/bulk-special', request);
  return response;
};

/**
 * Bulk update channel status for multiple stores
 */
export const bulkUpdateChannels = async (
  request: BulkChannelRequest
): Promise<BulkOperationResponse> => {
  const response = await NetworkManager.post('stores/channels/bulk-update', request);
  return response;
};

/**
 * Retry failed bulk channel updates
 */
export const retryBulkChannels = async (
  originalRequestId: string,
  storeNumbers: string[],
  channel: string,
  activate: boolean,
  deactivationReason?: string,
  deactivationExpiry?: string
): Promise<BulkOperationResponse> => {
  const response = await NetworkManager.post('stores/channels/bulk-retry', {
    originalRequestId,
    storeNumbers,
    channel,
    activate,
    deactivationReason,
    deactivationExpiry,
  });
  return response;
};

// Audit APIs
export * from './audit';